import { forwardRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Link from 'next/link';

import * as Accent from '@/components/common/Accent';
import {
  actionButtonColours,
  buttonColoursV2,
  complementaryButton,
  complementaryFont,
} from '@/components/common/complementaryColours';
import {
  determineDestination,
  VisuallyHidden,
} from '@/components/common/utils';
import ArrowSvg from '@/public/images/arrow-button.svg';
import ChevronRightIconSvg from '@/public/images/chevron-right.svg';

import type { HTMLProps, MouseEvent, MutableRefObject, ReactNode } from 'react';

export type ButtonVariant = 'solid' | 'text' | 'default';

export type ActionButtonVariant = 'flat' | 'text';

interface BaseButtonProps extends HTMLProps<HTMLButtonElement> {
  ariaLabel?: string;
  className?: string;
  children?: ReactNode;
  onClick?: (event: MouseEvent) => void;
  type?: 'submit' | 'button';
}
interface BaseButtonLinkProps {
  ariaLabel?: string;
  className?: string;
  href?: string;
  external?: boolean;
  children?: ReactNode;
}

const BaseButton = ({
  ariaLabel,
  className,
  children,
  onClick,
  type = 'button',
  ...props
}: BaseButtonProps) => (
  <button
    aria-label={ariaLabel}
    type={type === 'submit' ? 'submit' : 'button'}
    className={className}
    onClick={onClick}
    {...props}
  >
    {children}
  </button>
);

const BaseButtonLink = ({
  ariaLabel,
  className,
  href,
  external,
  children,
  hasIcon,
  variant,
  tabIndex,
  icon = <ChevronRight variant={variant} />,
  iconPosition = 'right',
  ariaCurrent,
}: BaseButtonLinkProps & {
  hasIcon?: boolean;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  variant?: ButtonVariant;
  tabIndex?: number;
  ariaCurrent?: 'page' | null;
}) => {
  const destination = determineDestination(href, external);
  return (
    <Anchor
      href={href}
      target={destination.target}
      className={className}
      aria-label={ariaLabel ? `${ariaLabel} ${destination.newTabText}` : null}
      rel={destination.isExternalLink ? 'external noopener' : null}
      tabIndex={tabIndex}
      aria-current={ariaCurrent}
    >
      {hasIcon && iconPosition === 'left' && icon}
      {children}
      {destination.isExternalLink && !ariaLabel && (
        <VisuallyHidden>{destination.newTabText}</VisuallyHidden>
      )}
      {hasIcon && iconPosition === 'right' && icon}
    </Anchor>
  );
};

// eslint-disable-next-line react/display-name
export const BaseArrowButton = forwardRef<HTMLButtonElement, BaseButtonProps>(
  (
    { ariaLabel, children, className, onClick, type = 'button', ...props },
    ref,
  ) => (
    <button
      aria-label={ariaLabel}
      ref={ref}
      className={className}
      type={type === 'submit' ? 'submit' : 'button'}
      onClick={onClick}
      {...props}
    >
      {children ? <span>{children}</span> : null}
      <ArrowSvg aria-hidden="true" />
    </button>
  ),
);

const BaseArrowButtonLink = ({
  ariaLabel,
  className,
  href,
  external,
  children,
}: BaseButtonLinkProps) => {
  const destination = determineDestination(href, external);
  return (
    <Anchor
      href={href}
      target={destination.target}
      className={className}
      aria-label={ariaLabel ? `${ariaLabel} ${destination.newTabText}` : null}
      rel={destination.isExternalLink ? 'external noopener' : null}
    >
      {children ? (
        <span>
          {children}
          {destination.isExternalLink && !ariaLabel && (
            <VisuallyHidden>{destination.newTabText}</VisuallyHidden>
          )}
        </span>
      ) : null}
      <ArrowSvg aria-hidden="true" />
    </Anchor>
  );
};

const BaseArrowButtonLeft = ({
  ariaLabel,
  className,
  children,
  onClick,
  type = 'button',
  ...props
}: BaseButtonProps) => (
  <button
    aria-label={ariaLabel}
    type={type === 'submit' ? 'submit' : 'button'}
    className={className}
    onClick={onClick}
    {...props}
  >
    <ArrowSvg aria-hidden="true" />
    {children ? <span>{children}</span> : null}
  </button>
);

const BaseArrowButtonLinkLeft = ({
  ariaLabel,
  className,
  href,
  external,
  children,
}: BaseButtonLinkProps) => {
  const destination = determineDestination(href, external);
  return (
    <Anchor
      href={href}
      target={destination.target}
      className={className}
      aria-label={ariaLabel ? `${ariaLabel} ${destination.newTabText}` : null}
      rel={destination.isExternalLink ? 'external noopener' : null}
    >
      <ArrowSvg aria-hidden="true" />
      {children ? (
        <span>
          {children}
          {destination.isExternalLink && !ariaLabel && (
            <VisuallyHidden>{destination.newTabText}</VisuallyHidden>
          )}
        </span>
      ) : null}
    </Anchor>
  );
};

interface ButtonProps {
  large?: boolean;
  className?: string;
  backgroundOfParent?: string;
}

interface ArrowButtonProps {
  className?: string;
  backgroundOfParent?: string;
  ref?: MutableRefObject<HTMLElement | null>;
}

const themeStyle = (p) => {
  const { border, backgroundColour, backgroundOnHover } = complementaryButton(
    p.backgroundOfParent,
  );
  const fontColour = complementaryFont(p.backgroundOfParent);

  return css`
    background-color: var(--colour-${backgroundColour});
    color: var(--colour-${fontColour});
    border-color: var(--colour-${border});

    &:hover {
      background-color: var(--colour-${backgroundOnHover});
      color: var(--colour-${complementaryFont(backgroundOnHover)});
      border-color: var(--colour-${backgroundOnHover});
    }
  `;
};

const Anchor = styled(Link)`
  text-decoration: none;
`;

export const CommonStyle = css`
  display: flex;
  align-items: center;
  width: max-content;
  vertical-align: middle;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  // For better browser compatibility / user experience:
  // https://www.sarasoueidan.com/blog/focus-indicators/
  &:focus:not(:focus-visible) {
    outline: none;
  }
  &:focus,
  &:focus-visible {
    outline: 2px solid var(--colour-button-focus);
    outline-offset: 2px;
  }
`;

const CommonArrowButtonBaseStyle = css`
  ${CommonStyle};

  border: none;
  font-size: var(--paragraph-sm);
  padding: 0;
  background: transparent;

  span {
    transition: all 0.2s ease;
    padding-bottom: 1px;
    border-bottom: 1px solid transparent;
  }

  svg {
    margin-left: var(--button-spacer-m);
    margin-right: var(--button-spacer-m);
    transition:
      fill 0.2s ease,
      transform 0.2s ease;

    path {
      transition: stroke 0.2s ease;
    }
  }
`;

const CommonArrowButtonStyle = css`
  ${CommonArrowButtonBaseStyle}

  &:hover {
    svg {
      transform: translateX(10px);
    }
  }
`;

const CommonArrowButtonLeftStyle = css`
  ${CommonArrowButtonBaseStyle}

  svg {
    transform: scaleX(-1);
  }

  &:hover {
    svg {
      transform: scaleX(-1) translateX(10px);
    }
  }
`;

export const Button = styled(BaseButton)<ButtonProps>`
  ${CommonStyle};

  border: 1px solid;
  border-radius: var(--button-border-radius);

  font-size: ${(p) => (p.large ? 'var(--paragraph-sm)' : 'var(--paragraph-s)')};
  padding: ${(p) =>
    p.large
      ? 'var(--button-spacer-l) var(--button-spacer-xl)'
      : 'var(--button-spacer-s) var(--button-spacer-l)'};

  ${(p) => themeStyle(p)}
`;

export const ButtonLink = styled(BaseButtonLink)<ButtonProps>`
  ${CommonStyle};

  border: 1px solid;
  border-radius: var(--button-border-radius);

  font-size: ${(p) => (p.large ? 'var(--paragraph-sm)' : 'var(--paragraph-s)')};
  padding: ${(p) =>
    p.large
      ? 'var(--button-spacer-l) var(--button-spacer-xl)'
      : 'var(--button-spacer-sm) var(--button-spacer-l)'};

  ${(p) => themeStyle(p)}
`;

export const ArrowButton = styled(BaseArrowButton)<ArrowButtonProps>`
  ${CommonArrowButtonStyle}

  color: ${(p) => `var(--colour-${complementaryFont(p.backgroundOfParent)})`};

  svg path {
    stroke: currentColor;
  }

  &:hover {
    span {
      border-color: currentColor;
    }

    svg {
      fill: currentColor;

      path:last-child {
        stroke: ${(p) =>
          p.backgroundOfParent
            ? `var(--colour-${p.backgroundOfParent})`
            : 'var(--colour-white)'};
      }
    }
  }
`;

export const ArrowButtonLink = styled(BaseArrowButtonLink)<ArrowButtonProps>`
  ${CommonArrowButtonStyle}

  color: ${(p) => `var(--colour-${complementaryFont(p.backgroundOfParent)})`};

  svg path {
    stroke: currentColor;
  }

  &:hover {
    span {
      border-color: currentColor;
    }

    svg {
      fill: currentColor;

      path:last-child {
        stroke: ${(p) =>
          p.backgroundOfParent
            ? `var(--colour-${p.backgroundOfParent})`
            : 'var(--colour-white)'};
      }
    }
  }
`;

export const ArrowButtonLeft = styled(BaseArrowButtonLeft)<ArrowButtonProps>`
  ${CommonArrowButtonLeftStyle}

  color: ${(p) => `var(--colour-${complementaryFont(p.backgroundOfParent)})`};

  svg path {
    stroke: currentColor;
  }

  &:hover {
    span {
      border-color: currentColor;
    }

    svg {
      fill: currentColor;

      path:last-child {
        stroke: ${(p) =>
          p.backgroundOfParent
            ? `var(--colour-${p.backgroundOfParent})`
            : 'var(--colour-white)'};
      }
    }
  }
`;

export const ArrowButtonLinkLeft = styled(
  BaseArrowButtonLinkLeft,
)<ArrowButtonProps>`
  ${CommonArrowButtonLeftStyle};

  color: ${(p) => `var(--colour-${complementaryFont(p.backgroundOfParent)})`};

  svg path {
    stroke: currentColor;
  }

  &:hover {
    span {
      border-color: currentColor;
    }

    svg {
      fill: currentColor;

      path:last-child {
        stroke: ${(p) =>
          p.backgroundOfParent
            ? `var(--colour-${p.backgroundOfParent})`
            : 'var(--colour-white)'};
      }
    }
  }
`;

// Action button without anchor
interface ActionButtonProps extends ButtonProps {
  children: ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const ActionButton = ({
  children,
  onClick,
  ...props
}: ActionButtonProps) => (
  <StyledActionButton type="button" {...props} onClick={onClick}>
    {children}
  </StyledActionButton>
);

export const StyledActionButton = styled.button<ButtonProps>`
  ${CommonStyle};

  border: 1px solid;
  border-radius: var(--button-border-radius);

  font-size: ${(p) => (p.large ? 'var(--paragraph-sm)' : 'var(--paragraph-s)')};
  padding: ${(p) =>
    p.large
      ? 'var(--button-spacer-l) var(--button-spacer-xl)'
      : 'var(--button-spacer-s) var(--button-spacer-l)'};

  background-color: ${(p) =>
    p.backgroundOfParent === 'deep-space'
      ? 'var(--colour-deep-space)'
      : 'var(--colour-white)'};
  color: ${(p) =>
    p.backgroundOfParent === 'deep-space'
      ? 'var(--colour-white)'
      : 'var(--colour-deep-space)'};
  border-color: var(--colour-deep-space);

  &:hover {
    background-color: ${(p) =>
      p.backgroundOfParent === 'deep-space'
        ? 'var(--colour-white)'
        : 'var(--colour-deep-space)'};
    color: ${(p) =>
      p.backgroundOfParent === 'deep-space'
        ? 'var(--colour-deep-space)'
        : 'var(--colour-white)'};
    border-color: var(--colour-deep-space);
  }
`;

const SmallButton = css`
  font-size: var(--label-sm);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-md);

  padding: 11px 16px 11px 16px;
  max-height: 40px;
`;

const MediumButton = css`
  font-size: var(--label-md);
  font-weight: var(--font-weight-bold);
  line-height: 22px;

  padding: 14px 20px 14px 20px;
  max-height: 48px;
`;

const SmallIconButton = css`
  padding: 11px 14px 11px 17px;
`;

const MediumIconButton = css`
  padding: 14px 20px 14px 21px;
`;

const ChevronRight = styled(ChevronRightIconSvg)`
  && {
    height: 13px;
    margin-left: 8px;
    width: auto;
  }
`;

export const ButtonLinkV2 = styled(BaseButtonLink)<
  BaseButtonLinkProps & {
    variant: ButtonVariant;
    backgroundOfParent: string;
    size?: 'sm' | 'md';
  }
>`
  --icon-size: ${(p) => (p?.size === 'md' ? '24px' : '20px')};
  --icon-margin: ${(p) => (p.size === 'md' ? '4px' : '2px')};
  --icon-margin-right: ${(p) =>
    p.iconPosition === 'left' ? 'var(--icon-margin)' : 0};
  --icon-margin-left: ${(p) =>
    p.iconPosition === 'left' ? 0 : 'var(--icon-margin)'};

  ${CommonStyle};
  ${(p) => (p?.size === 'md' ? MediumButton : SmallButton)}
  ${(p) => (p.hasIcon && p?.size === 'md' ? MediumIconButton : SmallIconButton)}
  border-radius: var(--button-border-radius);

  ${(p) => {
    const buttonColour = p.backgroundOfParent
      ? buttonColoursV2[p.backgroundOfParent][p.variant]
      : null;
    const outlineColour =
      p.backgroundOfParent === 'inverse-subtle-dark'
        ? 'var(--colour-button-focus-light)'
        : 'var(--colour-button-focus)';

    return `
      color: var(--colour-${buttonColour.fontColour});
      background-color: var(--colour-${buttonColour.backgroundColour});

      &:hover {
        background-color: var(--colour-${buttonColour.backgroundOnHover});
        text-decoration: ${buttonColour.underlineOnHover};
      }

      &:focus, &:focus-visible {
        outline-color: ${outlineColour};
      }
    `;
  }}

  svg {
    margin-right: var(--icon-margin-right);
    margin-left: var(--icon-margin-left);
    height: var(--icon-size);
    width: var(--icon-size);
  }

  path {
    fill: currentColor;
  }
`;

interface ActionButtonV2Props extends ActionButtonProps {
  as?: 'button' | 'span';
  disabled?: boolean;
  variant: ActionButtonVariant;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  background?: 'light' | 'dark';
  size: 'sm' | 'md' | 'lg';
}

export const StyledActionButtonV2 = styled.button<ActionButtonV2Props>`
  ${CommonStyle};
  border-radius: var(--button-border-radius);

  ${(p) => {
    switch (p.size) {
      case 'sm':
        return css`
          ${SmallActionButton};
          padding-left: ${p.leftIcon ? '10px' : '20px'};
          padding-right: ${p.rightIcon ? '10px' : '20px'};
          gap: 2px;
          > svg {
            height: 20px;
            width: 20px;
          }
        `;
      case 'md':
        return css`
          ${MediumActionButton};
          padding-left: ${p.leftIcon ? '12px' : '20px'};
          padding-right: ${p.rightIcon ? '12px' : '20px'};
          gap: 4px;
          > svg {
            height: 24px;
            width: 24px;
          }
        `;
      case 'lg':
        return css`
          ${LargeActionButton};
          padding-left: ${p.leftIcon ? '14px' : '24px'};
          padding-right: ${p.rightIcon ? '14px' : '24px'};
          gap: 6px;
          > svg {
            height: 28px;
            width: 28px;
          }
        `;
      default:
        return '';
    }
  }};

  ${(p) => {
    const buttonVariant =
      actionButtonColours[p.background ? p.background : 'light'][p.variant];
    return `
      color: var(--colour-${buttonVariant.fontColour});
      background-color: var(--colour-${buttonVariant.backgroundColour});
      border-color: var(--colour-transparent);
      opacity: ${p.disabled && '24%'};

      &:hover {
        ${
          p.disabled
            ? `cursor: auto;`
            : `background-color: var(--colour-${buttonVariant.backgroundOnHover});`
        }
        ${p.variant === 'text' && `text-decoration: underline`}
      }

      svg path {
        fill: currentColor;
      }
    `;
  }}
`;

const SmallActionButton = css`
  font-size: var(--label-sm);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-md);

  padding: 10px 20px 10px 20px;
  max-height: 40px;
`;

const MediumActionButton = css`
  font-size: var(--label-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--subheading-md);

  padding: 13px 20px 13px 20px;
  max-height: 48px;
`;

const LargeActionButton = css`
  font-size: var(--label-lg);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-lg);

  padding: 16px 24px 16px 24px;
  max-height: 56px;
`;
export const ActionButtonV2 = ({
  children,
  onClick,
  size,
  leftIcon,
  rightIcon,
  disabled,
  ...props
}: ActionButtonV2Props) => {
  return (
    <StyledActionButtonV2
      type="button"
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      size={size}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {leftIcon && leftIcon}
      {children}
      {rightIcon && rightIcon}
    </StyledActionButtonV2>
  );
};

interface HighlightedActionButtonV2Props extends ActionButtonV2Props {
  highlightColour: string;
  highlightStyle: string;
}

export const HighlightedActionButtonV2 = ({
  as = 'button',
  children,
  onClick,
  size,
  leftIcon,
  rightIcon,
  disabled,
  highlightColour,
  highlightStyle,
  ...props
}: HighlightedActionButtonV2Props) => {
  return (
    <StyledActionButtonV2
      as={as}
      type="button"
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      size={size}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {leftIcon && leftIcon}
      <HighlightedTextContainer>
        {children}
        <TextHighlight
          colour={highlightColour}
          highlightStyle={highlightStyle}
        />
      </HighlightedTextContainer>
      {rightIcon && rightIcon}
    </StyledActionButtonV2>
  );
};

const HighlightedTextContainer = styled.span`
  width: max-content;
  position: relative;
  display: inline-flex;
`;

const TextHighlight = ({
  colour,
  highlightStyle,
}: {
  colour: string;
  highlightStyle: string;
}) => {
  switch (highlightStyle) {
    case 'arc':
      return (
        <HighlightContainer colour={colour} highlightStyle={highlightStyle}>
          <Accent.Emphasise variant="arc" />
        </HighlightContainer>
      );
    case 'double':
      return (
        <HighlightContainer colour={colour} highlightStyle={highlightStyle}>
          <Accent.Underline variant="double" />
        </HighlightContainer>
      );
    case 'squiggle':
      return (
        <HighlightContainer colour={colour} highlightStyle={highlightStyle}>
          <Accent.Underline variant="wave" />
        </HighlightContainer>
      );

    case 'thick':
      return (
        <HighlightContainer colour={colour} highlightStyle={highlightStyle}>
          <Accent.Underline variant="thick" />
        </HighlightContainer>
      );
    case 'underline':
      return (
        <HighlightContainer colour={colour} highlightStyle={highlightStyle}>
          <Accent.Underline variant="short" />
        </HighlightContainer>
      );
    default:
      return null;
  }
};

const HighlightContainer = styled.span<{
  colour: string;
  highlightStyle: string;
}>`
  display: flex;
  position: absolute;
  pointer-events: none;
  color: ${(p) => `var(--colour-${p.colour})`};
  ${(p) => {
    switch (p.highlightStyle) {
      case 'arc':
        return css`
          max-width: 100%;
          height: 0.8em;
          left: 1em;
          right: 1em;
          bottom: 1em;
        `;
      case 'double':
        return css`
          max-width: 100%;
          height: 0.375em;
          left: 1em;
          right: 1em;
          bottom: -1em;
        `;
      case 'squiggle':
        return css`
          max-width: 100%;
          height: 0.625em;
          left: 1em;
          right: 1em;
          bottom: -1em;
        `;

      case 'thick':
        return css`
          max-width: 100%;
          height: 0.4em;
          left: 1em;
          right: 1em;
          bottom: -1em;
        `;
      case 'underline':
        return css`
          max-width: 100%;
          height: 0.25em;
          bottom: -0.625em;
        `;
      default:
        return '';
    }
  }};
`;
